import { useEffect, useRef, useState } from 'react'
import { Button, Input, Popover, Space } from 'antd'
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { observer } from 'mobx-react-lite'
import {
    MdBorderColor,
    MdBorderTop,
    MdDeleteSweep,
    MdFormatBold,
    MdFormatColorText,
    MdFormatItalic,
    MdFormatStrikethrough,
    MdFormatUnderlined,
    MdFormatListBulleted,
    MdFormatListNumbered,
    MdGridOff,
    MdGridOn,
    MdHorizontalRule,
    MdLink,
    MdPlaylistRemove,
    MdRedo,
    MdTableRows,
    MdUndo,
    MdViewColumn,
} from 'react-icons/md'

import { LETTER_EXTENSIONS } from './letterExtensions'
import { HIGHLIGHT_COLORS, TEXT_COLORS } from './richTextPalette'
import { TABLE_EXTENSIONS } from './tableExtensions'
import { isRichTextEmpty, normalizeRichTextValue, RICH_TEXT_PLACEHOLDER } from './richTextUtils'
import './rich-text.css'

import type { FieldMethods } from '@/Core/UI/Form/types'
import type { ReactNode } from 'react'

interface RichTextInputProps {
    field?: FieldMethods
    index?: number
}

/**
 * Набір кнопок панелі (`inputProps.toolbar` поля):
 * - `basic` — жирний, курсив, закреслений, заголовки, списки (за замовчуванням);
 * - `letter` — те саме плюс підкреслений, посилання й роздільник (ADR 036,
 *   `SPEC-013` §9.6), колір тексту й маркер (Q13.5): тіло шаблону листа й
 *   самого листа.
 *
 * Підкреслення, посилання й роздільник входять у StarterKit 3; колір тексту
 * й маркер — окремі пакети tiptap (`extension-text-style`, `extension-color`,
 * `extension-highlight`, згода замовника 2026-10-14).
 *
 * - `description` — `basic` плюс роздільник і таблиця (Q2.8, `SPEC-025`
 *   §3.6.1): опис запиту, тендера й заявки. Таблиці — пакети
 *   `@tiptap/extension-table*` ({@link TABLE_EXTENSIONS}); бек пропускає їх
 *   лише профілем `RichTextSanitizer::sanitizeWithTables()`.
 */
export type RichTextToolbarPreset = 'basic' | 'letter' | 'description'

const TOOLBAR_PRESETS: readonly RichTextToolbarPreset[] = ['basic', 'letter', 'description']

/** Пресет панелі з `inputProps.toolbar`; невідоме значення — `basic`. */
const resolveToolbarPreset = (value: unknown): RichTextToolbarPreset =>
    TOOLBAR_PRESETS.find((preset) => preset === value) ?? 'basic'

/** Розширення понад StarterKit для пресета — однаково в редакторі й тестах. */
const presetExtensions = (preset: RichTextToolbarPreset) => {
    if (preset === 'letter') return LETTER_EXTENSIONS
    if (preset === 'description') return TABLE_EXTENSIONS

    return []
}

/** Схеми посилань — ті самі, що пропускає санітайзер беку (`RichTextSanitizer::LINK_SCHEMES`). */
const LINK_SCHEMES = /^(https?:\/\/|mailto:)/i

interface ToolbarButtonProps {
    onClick: () => void
    active?: boolean
    disabled?: boolean
    title: string
    text?: boolean
    children: ReactNode
}

const ToolbarButton = ({ onClick, active, disabled, title, text, children }: ToolbarButtonProps) => (
    <button
        type="button"
        className={`rich-text__btn${text ? ' rich-text__btn--text' : ''}${active ? ' is-active' : ''}`}
        title={title}
        aria-label={title}
        aria-pressed={active === true}
        disabled={disabled === true}
        // onMouseDown preventDefault: не даємо редактору втратити фокус/виділення при кліку по кнопці
        onMouseDown={(e) => e.preventDefault()}
        onClick={onClick}
    >
        {children}
    </button>
)

/**
 * Кнопка «Посилання»: адреса в `Popover`. Порожня адреса знімає посилання;
 * адреса без схеми отримує `https://` — санітайзер беку відносних посилань не
 * пропускає, і людина інакше мовчки втратила б посилання після збереження.
 */
const LinkButton = ({ editor }: { editor: Editor }) => {
    const [open, setOpen] = useState(false)
    const [url, setUrl] = useState('')
    const active = useEditorState({ editor, selector: ({ editor: e }) => e.isActive('link') })

    const apply = () => {
        const value = url.trim()
        const chain = editor.chain().focus().extendMarkRange('link')

        if (value === '') {
            chain.unsetLink().run()
        } else {
            chain.setLink({ href: LINK_SCHEMES.test(value) ? value : `https://${value}` }).run()
        }

        setOpen(false)
    }

    return (
        <Popover
            open={open}
            trigger="click"
            onOpenChange={(next) => {
                if (next) setUrl(String(editor.getAttributes('link')['href'] ?? ''))
                setOpen(next)
            }}
            content={
                <Space.Compact style={{ width: 320 }}>
                    <Input
                        autoFocus
                        placeholder="https://"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        onPressEnter={apply}
                    />
                    <Button type="primary" onClick={apply}>
                        OK
                    </Button>
                    {active ? (
                        <Button
                            onClick={() => {
                                editor.chain().focus().extendMarkRange('link').unsetLink().run()
                                setOpen(false)
                            }}
                        >
                            Прибрати
                        </Button>
                    ) : null}
                </Space.Compact>
            }
        >
            {/* Обгортка — DOM-вузол для позиціювання Popover: кнопка панелі ref не пробрасує */}
            <span className="rich-text__btn-wrap">
                <ToolbarButton title="Посилання" active={active} onClick={() => undefined}>
                    <MdLink />
                </ToolbarButton>
            </span>
        </Popover>
    )
}

type ColorKind = 'color' | 'highlight'

const COLOR_KIND = {
    color: { title: 'Колір тексту', reset: 'Без кольору', swatches: TEXT_COLORS, icon: <MdFormatColorText /> },
    highlight: { title: 'Маркер', reset: 'Без маркера', swatches: HIGHLIGHT_COLORS, icon: <MdBorderColor /> },
} as const

/**
 * Кнопка «Колір тексту» / «Маркер»: палітра кольорів теми в `Popover`.
 * Смужка під іконкою — поточний колір виділення.
 */
const ColorButton = ({ editor, kind }: { editor: Editor; kind: ColorKind }) => {
    const [open, setOpen] = useState(false)
    const { title, reset, swatches, icon } = COLOR_KIND[kind]
    const current = useEditorState({
        editor,
        selector: ({ editor: e }): string | null => {
            const value = kind === 'color' ? e.getAttributes('textStyle')['color'] : e.getAttributes('highlight')['color']

            return typeof value === 'string' && value !== '' ? value : null
        },
    })

    const apply = (color: string | null) => {
        const chain = editor.chain().focus()

        if (kind === 'color') {
            ;(color === null ? chain.unsetColor() : chain.setColor(color)).run()
        } else {
            ;(color === null ? chain.unsetHighlight() : chain.setHighlight({ color })).run()
        }

        setOpen(false)
    }

    return (
        <Popover
            open={open}
            trigger="click"
            onOpenChange={setOpen}
            content={
                <div className="rich-text__palette" role="group" aria-label={title}>
                    {swatches.map((swatch) => (
                        <button
                            key={swatch.color}
                            type="button"
                            className={`rich-text__swatch${current === swatch.color ? ' is-active' : ''}`}
                            title={swatch.title}
                            aria-label={`${title}: ${swatch.title}`}
                            // Колір зразка — це дані листа, а не тема: у листі він той самий
                            style={{ background: swatch.color }}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => apply(swatch.color)}
                        />
                    ))}
                    <Button size="small" type="text" onMouseDown={(e) => e.preventDefault()} onClick={() => apply(null)}>
                        {reset}
                    </Button>
                </div>
            }
        >
            {/* Обгортка — DOM-вузол для позиціювання Popover: кнопка панелі ref не пробрасує */}
            <span className="rich-text__btn-wrap">
                <ToolbarButton title={title} active={current !== null} onClick={() => undefined}>
                    <span className="rich-text__color-icon">
                        {icon}
                        <span className="rich-text__color-bar" style={current !== null ? { background: current } : {}} />
                    </span>
                </ToolbarButton>
            </span>
        </Popover>
    )
}

/**
 * Кнопки таблиці (пресет `description`): «Таблиця» вставляє 2×3 без
 * заголовка — як легасі (`EditorJsInput/Tools/table.js`); решта з'являється,
 * лише коли курсор у таблиці.
 */
const TableButtons = ({ editor }: { editor: Editor }) => {
    const inTable = useEditorState({ editor, selector: ({ editor: e }) => e.isActive('table') })
    const headerRow = useEditorState({ editor, selector: ({ editor: e }) => e.isActive('tableHeader') })

    return (
        <>
            <ToolbarButton
                title="Таблиця"
                disabled={inTable}
                onClick={() => editor.chain().focus().insertTable({ rows: 2, cols: 3, withHeaderRow: false }).run()}
            >
                <MdGridOn />
            </ToolbarButton>
            {inTable ? (
                <>
                    <ToolbarButton title="Рядок нижче" onClick={() => editor.chain().focus().addRowAfter().run()}>
                        <MdTableRows />
                    </ToolbarButton>
                    <ToolbarButton
                        title="Стовпець праворуч"
                        onClick={() => editor.chain().focus().addColumnAfter().run()}
                    >
                        <MdViewColumn />
                    </ToolbarButton>
                    <ToolbarButton title="Видалити рядок" onClick={() => editor.chain().focus().deleteRow().run()}>
                        <MdPlaylistRemove />
                    </ToolbarButton>
                    <ToolbarButton
                        title="Видалити стовпець"
                        onClick={() => editor.chain().focus().deleteColumn().run()}
                    >
                        <MdDeleteSweep />
                    </ToolbarButton>
                    <ToolbarButton
                        title="Рядок-заголовок"
                        active={headerRow}
                        onClick={() => editor.chain().focus().toggleHeaderRow().run()}
                    >
                        <MdBorderTop />
                    </ToolbarButton>
                    <ToolbarButton title="Видалити таблицю" onClick={() => editor.chain().focus().deleteTable().run()}>
                        <MdGridOff />
                    </ToolbarButton>
                </>
            ) : null}
        </>
    )
}

const Toolbar = ({ editor, preset }: { editor: Editor; preset: RichTextToolbarPreset }) => (
    <div className="rich-text__toolbar">
        <ToolbarButton
            title="Жирний"
            active={editor.isActive('bold')}
            onClick={() => editor.chain().focus().toggleBold().run()}
        >
            <MdFormatBold />
        </ToolbarButton>
        <ToolbarButton
            title="Курсив"
            active={editor.isActive('italic')}
            onClick={() => editor.chain().focus().toggleItalic().run()}
        >
            <MdFormatItalic />
        </ToolbarButton>
        {preset === 'letter' ? (
            <ToolbarButton
                title="Підкреслений"
                active={editor.isActive('underline')}
                onClick={() => editor.chain().focus().toggleUnderline().run()}
            >
                <MdFormatUnderlined />
            </ToolbarButton>
        ) : null}
        <ToolbarButton
            title="Закреслений"
            active={editor.isActive('strike')}
            onClick={() => editor.chain().focus().toggleStrike().run()}
        >
            <MdFormatStrikethrough />
        </ToolbarButton>

        <span className="rich-text__sep" />

        <ToolbarButton
            title="Заголовок 2"
            text
            active={editor.isActive('heading', { level: 2 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
            H2
        </ToolbarButton>
        <ToolbarButton
            title="Заголовок 3"
            text
            active={editor.isActive('heading', { level: 3 })}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
            H3
        </ToolbarButton>

        <span className="rich-text__sep" />

        <ToolbarButton
            title="Маркований список"
            active={editor.isActive('bulletList')}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
            <MdFormatListBulleted />
        </ToolbarButton>
        <ToolbarButton
            title="Нумерований список"
            active={editor.isActive('orderedList')}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
            <MdFormatListNumbered />
        </ToolbarButton>

        {preset === 'letter' ? (
            <>
                <span className="rich-text__sep" />
                <ColorButton editor={editor} kind="color" />
                <ColorButton editor={editor} kind="highlight" />
                <span className="rich-text__sep" />
                <LinkButton editor={editor} />
                <ToolbarButton title="Роздільник" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
                    <MdHorizontalRule />
                </ToolbarButton>
            </>
        ) : null}

        {preset === 'description' ? (
            <>
                <span className="rich-text__sep" />
                <ToolbarButton title="Роздільник" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
                    <MdHorizontalRule />
                </ToolbarButton>
                <TableButtons editor={editor} />
            </>
        ) : null}

        <span className="rich-text__sep" />

        <ToolbarButton
            title="Скасувати"
            disabled={!editor.can().undo()}
            onClick={() => editor.chain().focus().undo().run()}
        >
            <MdUndo />
        </ToolbarButton>
        <ToolbarButton
            title="Повторити"
            disabled={!editor.can().redo()}
            onClick={() => editor.chain().focus().redo().run()}
        >
            <MdRedo />
        </ToolbarButton>
    </div>
)

/**
 * RichTextInput — tiptap-редактор rich-text поля.
 *
 * Підключається як кастомний `field.input` ресурсу (edit/create). Параметри поля
 * (`inputProps`): `placeholder` — текст порожнього редактора, `toolbar` — пресет
 * панелі ({@link RichTextToolbarPreset}). Зберігає
 * HTML у полі `description` через `onUpdate → getHTML()`; порожній опис зводимо до `''`
 * (`normalizeRichTextValue`), щоб спрацював ядровий `isRequired`. Донор-патерн —
 * autostrada `ChatWindow/TiptapEditor.tsx` (без mention/link/suggestion — це чат).
 */
export const RichTextInput = observer(({ field, index }: RichTextInputProps) => {
    // Хук викликаємо ДО раннього return: значення читаємо з bind (реактивно до value).
    const bound = field?.bind({ index: index ?? 0 })
    const initialHtml = typeof bound?.value === 'string' ? bound.value : ''
    // `inputProps` існує на рантайм-полі (BaseFieldStore), але контракт `FieldMethods`
    // його не декларує — читаємо через вузький каст (як роблять сусідні Ship-інпути).
    const fieldInputProps = (field as { inputProps?: Record<string, unknown> } | undefined)?.inputProps
    const disabled = fieldInputProps?.['disabled'] === true || field?.isEditable === false
    // Плейсхолдер і пресет панелі — з поля (ADR 036): один редактор на систему,
    // а текст-запрошення й кнопки в кожного поля свої
    const placeholder =
        typeof fieldInputProps?.['placeholder'] === 'string' ? fieldInputProps['placeholder'] : RICH_TEXT_PLACEHOLDER
    const preset = resolveToolbarPreset(fieldInputProps?.['toolbar'])

    // Ref на onChange, щоб onUpdate завжди мав свіжий колбек без перестворення редактора.
    const onChangeRef = useRef<((value: string) => void) | undefined>(bound?.onChange)
    onChangeRef.current = bound?.onChange

    const editor = useEditor({
        extensions: [
            // Посилання в редакторі не відкриваються кліком: клік — це редагування
            StarterKit.configure({ link: { openOnClick: false, autolink: true, defaultProtocol: 'https' } }),
            Placeholder.configure({ placeholder }),
            ...presetExtensions(preset),
        ],
        content: initialHtml,
        editable: !disabled,
        onUpdate: ({ editor: e }) => {
            onChangeRef.current?.(normalizeRichTextValue(e.getHTML()))
        },
        immediatelyRender: true,
    })

    // Синхронізуємо editable, якщо поле стало read-only після монтування.
    useEffect(() => {
        editor?.setEditable(!disabled)
    }, [editor, disabled])

    // Префіл у режимі редагування: значення поля може прийти асинхронно вже ПІСЛЯ монтування
    // (форма підвантажує задачу). Ставимо контент лише поки редактор ще порожній — щоб не
    // затерти те, що користувач уже набрав. emitUpdate:false — не викликати onUpdate з префілу.
    useEffect(() => {
        if (!editor) return
        if (isRichTextEmpty(initialHtml)) return
        if (!isRichTextEmpty(editor.getHTML())) return
        editor.commands.setContent(initialHtml, { emitUpdate: false })
    }, [editor, initialHtml])

    if (!field) return null

    return (
        <div className="rich-text">
            {editor && !disabled && <Toolbar editor={editor} preset={preset} />}
            <EditorContent editor={editor} className="rich-text__content" />
        </div>
    )
})
